import api, { getMutationRequestConfig } from '../utils/axiosConfig'
import type {
    ManualRecognitionTypeCreatePayload,
    ManualRecognitionModelTypeBindingSummary,
    ManualRecognitionModelTypeBindingsReplacePayload,
    ManualRecognitionTypeMappingSummary,
    ManualRecognitionTypeSummary,
    ManualRecognitionTypeUpdatePayload,
    ModelClassMappingSummary,
    DisplayCategoryBindingsReplacePayload,
    ModelConfigScopeType,
    ModelDisplayCategoryCreatePayload,
    ModelDisplayCategorySummary,
    ModelDisplayCategoryUpdatePayload,
    ModelDisplayCategoryVisibilityUpdatePayload,
    ModelEvaluationCreatePayload,
    ModelEvaluationImageResultListResponse,
    ModelEvaluationTaskDetail,
    ModelEvaluationTaskListResponse,
    ModelKey,
    ModelManagementProjectSummary,
    ModelProjectConfigKind,
    ModelProjectConfigRecordSummary,
    ModelProjectConfigSummary,
    ModelRawClassOptionSummary,
    ModelRuntimeConfigSummary,
    ModelRuntimeConfigUpdatePayload,
    ModelValidationResponse,
    ModelVersionClassDiffSummary,
    ModelVersionSummary,
    ModelVersionRemarkUpdatePayload,
    ModelVersionUploadPayload,
} from '../types/modelManagement'
import type {
    ModelEvaluationMatchListResponse,
    ModelEvaluationV3Report,
} from '../types/modelValidation'

const MODEL_UPLOAD_TIMEOUT_MS = 300000
const DEFAULT_SCOPE_TYPE: ModelConfigScopeType = 'platform'

interface ScopedQuery {
    scopeType?: ModelConfigScopeType
    projectId?: number | null
    effective?: boolean
}

export interface ModelVersionOperationLogContext {
    operationModule?: string
    operationAction?: string
}

const getModelVersionOperationLogParams = (
    context?: ModelVersionOperationLogContext
) => {
    if (!context?.operationModule && !context?.operationAction) {
        return undefined
    }
    return {
        ...(context.operationModule
            ? { operation_module: context.operationModule }
            : {}),
        ...(context.operationAction
            ? { operation_action: context.operationAction }
            : {}),
    }
}

const toBackendEvaluationPipeline = (
    pipeline: ModelEvaluationCreatePayload['baselinePipeline']
) =>
    pipeline
        ? {
              insect_version_id: pipeline.insectVersionId,
              mosquito_version_id: pipeline.mosquitoVersionId,
          }
        : undefined

export const getModelVersions = async (
    modelKey?: ModelKey
): Promise<ModelVersionSummary[]> => {
    const response = await api.get<ModelVersionSummary[]>(
        '/model-management/versions',
        {
            params: {
                model_key: modelKey,
            },
        }
    )
    return response.data
}

export const getModelDisplayCategories = async ({
    effective = false,
    projectId,
    scopeType = DEFAULT_SCOPE_TYPE,
}: ScopedQuery = {}): Promise<ModelDisplayCategorySummary[]> => {
    const response = await api.get<ModelDisplayCategorySummary[]>(
        '/model-management/display-categories',
        {
            params: {
                effective,
                project_id: projectId,
                scope_type: scopeType,
            },
        }
    )
    return response.data
}

export const createModelDisplayCategory = async (
    payload: ModelDisplayCategoryCreatePayload
): Promise<ModelDisplayCategorySummary> => {
    const response = await api.post<ModelDisplayCategorySummary>(
        '/model-management/display-categories',
        {
            display_name_zh_cn: payload.displayNameZhCn,
            color: payload.color,
            ...(payload.scopeType ? { scope_type: payload.scopeType } : {}),
            ...(payload.projectId !== undefined
                ? { project_id: payload.projectId }
                : {}),
            ...(payload.syncToProjects !== undefined
                ? { sync_to_projects: payload.syncToProjects }
                : {}),
        }
    )
    return response.data
}

export const updateModelDisplayCategory = async (
    categoryKey: string,
    payload: ModelDisplayCategoryUpdatePayload
): Promise<ModelDisplayCategorySummary> => {
    const requestPayload = {
        display_name_zh_cn: payload.displayNameZhCn,
        color: payload.color,
        ...(payload.scopeType ? { scope_type: payload.scopeType } : {}),
        ...(payload.projectId !== undefined ? { project_id: payload.projectId } : {}),
        ...(payload.syncToProjects !== undefined
            ? { sync_to_projects: payload.syncToProjects }
            : {}),
    }
    const response = await api.patch<ModelDisplayCategorySummary>(
        `/model-management/display-categories/${categoryKey}`,
        requestPayload
    )
    return response.data
}

export const deleteModelDisplayCategory = async (
    categoryKey: string,
    payload: { scopeType?: ModelConfigScopeType; projectId?: number | null } = {}
): Promise<void> => {
    const requestConfig =
        payload.scopeType === 'project'
            ? {
                  params: {
                      scope_type: payload.scopeType,
                      project_id: payload.projectId,
                  },
              }
            : undefined
    if (requestConfig) {
        await api.delete(
            `/model-management/display-categories/${categoryKey}`,
            requestConfig,
        )
        return
    }
    await api.delete(`/model-management/display-categories/${categoryKey}`)
}

export const updateModelDisplayCategoryVisibility = async (
    categoryKey: string,
    payload: ModelDisplayCategoryVisibilityUpdatePayload
): Promise<ModelDisplayCategorySummary> => {
    const response = await api.patch<ModelDisplayCategorySummary>(
        `/model-management/display-categories/${categoryKey}/visibility`,
        {
            visible_to_user: payload.visibleToUser,
        },
        {
            params: {
                project_id: payload.projectId,
                scope_type: payload.scopeType ?? DEFAULT_SCOPE_TYPE,
            },
        }
    )
    return response.data
}

export const getManualRecognitionTypes = async (): Promise<
    ManualRecognitionTypeSummary[]
> => {
    const response = await api.get<ManualRecognitionTypeSummary[]>(
        '/model-management/manual-recognition-types'
    )
    return response.data
}

export const createManualRecognitionType = async (
    payload: ManualRecognitionTypeCreatePayload
): Promise<ManualRecognitionTypeSummary> => {
    const response = await api.post<ManualRecognitionTypeSummary>(
        '/model-management/manual-recognition-types',
        {
            display_name_zh_cn: payload.displayNameZhCn,
            color: payload.color,
        }
    )
    return response.data
}

export const updateManualRecognitionType = async (
    manualTypeKey: string,
    payload: ManualRecognitionTypeUpdatePayload
): Promise<ManualRecognitionTypeSummary> => {
    const response = await api.patch<ManualRecognitionTypeSummary>(
        `/model-management/manual-recognition-types/${manualTypeKey}`,
        {
            display_name_zh_cn: payload.displayNameZhCn,
            color: payload.color,
        }
    )
    return response.data
}

export const deleteManualRecognitionType = async (
    manualTypeKey: string
): Promise<void> => {
    await api.delete(
        `/model-management/manual-recognition-types/${manualTypeKey}`
    )
}

export const updateModelRecognitionTypeColor = async (
    rawClassNorm: string,
    color: string
): Promise<ModelRawClassOptionSummary> => {
    const response = await api.patch<ModelRawClassOptionSummary>(
        `/model-management/raw-class-options/${encodeURIComponent(rawClassNorm)}`,
        { color }
    )
    return response.data
}

export const getManualRecognitionTypeMappings = async ({
    effective = false,
    projectId,
    scopeType = DEFAULT_SCOPE_TYPE,
}: ScopedQuery = {}): Promise<ManualRecognitionTypeMappingSummary[]> => {
    const response = await api.get<ManualRecognitionTypeMappingSummary[]>(
        '/model-management/manual-recognition-type-mappings',
        {
            params: {
                effective,
                project_id: projectId,
                scope_type: scopeType,
            },
        }
    )
    return response.data
}

export const getManualRecognitionModelTypeBindings = async (): Promise<
    ManualRecognitionModelTypeBindingSummary[]
> => {
    const response = await api.get<ManualRecognitionModelTypeBindingSummary[]>(
        '/model-management/manual-recognition-model-type-bindings'
    )
    return response.data
}

export const replaceManualRecognitionModelTypeBindings = async (
    manualTypeKey: string,
    payload: ManualRecognitionModelTypeBindingsReplacePayload
): Promise<void> => {
    await api.put(
        `/model-management/manual-recognition-types/${encodeURIComponent(manualTypeKey)}/model-type-bindings`,
        {
            bindings: payload.rawClassNorms.map((rawClassNorm) => ({
                raw_class_norm: rawClassNorm,
            })),
        },
        getMutationRequestConfig()
    )
}

export const getModelManagementProjects = async (): Promise<
    ModelManagementProjectSummary[]
> => {
    const response = await api.get<ModelManagementProjectSummary[]>(
        '/model-management/projects'
    )
    return response.data
}

export const getModelProjectConfig = async (
    projectId: number,
    configKind?: ModelProjectConfigKind
): Promise<ModelProjectConfigSummary> => {
    const response = await api.get<ModelProjectConfigSummary>(
        `/model-management/project-configs/${projectId}`,
        {
            params: {
                config_kind: configKind,
            },
        }
    )
    return response.data
}

export const getModelProjectConfigRecords = async (): Promise<
    ModelProjectConfigRecordSummary[]
> => {
    const response = await api.get<ModelProjectConfigRecordSummary[]>(
        '/model-management/project-configs'
    )
    return response.data
}

export const getScopedModelProjectConfigRecords = async (
    configKind: ModelProjectConfigKind
): Promise<ModelProjectConfigRecordSummary[]> => {
    const response = await api.get<ModelProjectConfigRecordSummary[]>(
        '/model-management/project-configs',
        {
            params: {
                config_kind: configKind,
            },
        }
    )
    return response.data
}

export const createModelProjectConfig = async (
    projectId: number
): Promise<ModelProjectConfigSummary> => {
    const response = await api.post<ModelProjectConfigSummary>(
        `/model-management/project-configs/${projectId}`
    )
    return response.data
}

export const createModelProjectTypeConfig = async (
    projectId: number
): Promise<ModelProjectConfigSummary> => {
    const response = await api.post<ModelProjectConfigSummary>(
        `/model-management/project-configs/${projectId}/type-bindings`
    )
    return response.data
}

export const deleteModelProjectConfig = async (
    projectId: number,
    operationAction?: string,
): Promise<ModelProjectConfigSummary> => {
    const response = await api.delete<ModelProjectConfigSummary>(
        `/model-management/project-configs/${projectId}`,
        {
            params: {
                operation_action: operationAction,
            },
        },
    )
    return response.data
}

export const deleteModelProjectTypeConfig = async (
    projectId: number
): Promise<ModelProjectConfigSummary> => {
    const response = await api.delete<ModelProjectConfigSummary>(
        `/model-management/project-configs/${projectId}/type-bindings`
    )
    return response.data
}

export const getModelRuntimeConfigs = async (): Promise<
    ModelRuntimeConfigSummary[]
> => {
    const response = await api.get<ModelRuntimeConfigSummary[]>(
        '/model-management/runtime-configs'
    )
    return response.data
}

export const getScopedModelRuntimeConfigs = async ({
    effective = false,
    projectId,
    scopeType = DEFAULT_SCOPE_TYPE,
}: ScopedQuery = {}): Promise<ModelRuntimeConfigSummary[]> => {
    const response = await api.get<ModelRuntimeConfigSummary[]>(
        '/model-management/runtime-configs',
        {
            params: {
                effective,
                project_id: projectId,
                scope_type: scopeType,
            },
        }
    )
    return response.data
}

export const updateModelRuntimeConfig = async (
    payload: ModelRuntimeConfigUpdatePayload
): Promise<ModelRuntimeConfigSummary> => {
    const response = await api.patch<ModelRuntimeConfigSummary>(
        `/model-management/runtime-configs/${payload.modelKey}`,
        {
            clear_current_version: payload.clearCurrentVersion,
            current_version_id: payload.currentVersionId,
            participate_in_inference: payload.participateInInference,
            participate_in_fusion: payload.participateInFusion,
            visible_to_user: payload.visibleToUser,
            save_raw_result: payload.saveRawResult,
        },
        {
            params: {
                operation_module: payload.operationModule,
                operation_action: payload.operationAction,
                project_id: payload.projectId,
                scope_type: payload.scopeType ?? DEFAULT_SCOPE_TYPE,
            },
        }
    )
    return response.data
}

export const getModelClassMappings = async (
    mappingSetId = 'default',
    scope: ScopedQuery = {}
): Promise<ModelClassMappingSummary[]> => {
    const response = await api.get<ModelClassMappingSummary[]>(
        '/model-management/class-mappings',
        {
            params: {
                effective: scope.effective ?? false,
                mapping_set_id: mappingSetId,
                project_id: scope.projectId,
                scope_type: scope.scopeType ?? DEFAULT_SCOPE_TYPE,
            },
        }
    )
    return response.data
}

export const getModelRawClassOptions = async (
    mappingSetId = 'default',
    modelKey?: ModelKey,
    scope: ScopedQuery = {}
): Promise<ModelRawClassOptionSummary[]> => {
    const response = await api.get<ModelRawClassOptionSummary[]>(
        '/model-management/raw-class-options',
        {
            params: {
                effective: scope.effective ?? false,
                mapping_set_id: mappingSetId,
                model_key: modelKey,
                project_id: scope.projectId,
                scope_type: scope.scopeType ?? DEFAULT_SCOPE_TYPE,
            },
        }
    )
    return response.data
}

export const getModelVersionClassDiff = async (
    versionId: number,
    baselineVersionId?: number | null
): Promise<ModelVersionClassDiffSummary> => {
    const response = await api.get<ModelVersionClassDiffSummary>(
        `/model-management/versions/${versionId}/class-diff`,
        {
            params: {
                baseline_version_id: baselineVersionId,
            },
        }
    )
    return response.data
}

export const replaceModelDisplayCategoryBindings = async (
    categoryKey: string,
    payload: DisplayCategoryBindingsReplacePayload
): Promise<void> => {
    await api.put(
        `/model-management/display-categories/${encodeURIComponent(categoryKey)}/bindings`,
        {
            scope_type: payload.scopeType ?? DEFAULT_SCOPE_TYPE,
            project_id: payload.projectId,
            raw_class_norms: payload.rawClassNorms,
            manual_type_keys: payload.manualTypeKeys,
        }
    )
}

export const uploadModelVersion = async (
    payload: ModelVersionUploadPayload
): Promise<ModelVersionSummary> => {
    const formData = new FormData()
    formData.append('model_key', payload.modelKey)
    formData.append('version_label', payload.versionLabel)
    if (payload.remark) {
        formData.append('remark', payload.remark)
    }
    formData.append('model_kind', payload.modelKind ?? 'detect')
    formData.append('file', payload.file)
    if (payload.modelLabels) {
        formData.append('model_labels', JSON.stringify(payload.modelLabels))
    }

    const response = await api.post<ModelVersionSummary>(
        '/model-management/versions/upload',
        formData,
        {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            ...getMutationRequestConfig({
                preventDuplicate: false,
            }),
            timeout: MODEL_UPLOAD_TIMEOUT_MS,
        }
    )
    return response.data
}

export const updateModelVersionRemark = async (
    versionId: number,
    payload: ModelVersionRemarkUpdatePayload
): Promise<ModelVersionSummary> => {
    const response = await api.patch<ModelVersionSummary>(
        `/model-management/versions/${versionId}/remark`,
        payload,
        getMutationRequestConfig()
    )
    return response.data
}

export const validateModelVersion = async (
    versionId: number
): Promise<ModelValidationResponse> => {
    const response = await api.post<ModelValidationResponse>(
        `/model-management/versions/${versionId}/validate`
    )
    return response.data
}

export const activateModelVersion = async (
    versionId: number,
    context?: ModelVersionOperationLogContext
): Promise<ModelVersionSummary> => {
    const params = getModelVersionOperationLogParams(context)
    const response = params
        ? await api.post<ModelVersionSummary>(
              `/model-management/versions/${versionId}/activate`,
              undefined,
              { params }
          )
        : await api.post<ModelVersionSummary>(
              `/model-management/versions/${versionId}/activate`
          )
    return response.data
}

export const deactivateModelVersion = async (
    versionId: number,
    context?: ModelVersionOperationLogContext
): Promise<ModelVersionSummary> => {
    const params = getModelVersionOperationLogParams(context)
    const response = params
        ? await api.post<ModelVersionSummary>(
              `/model-management/versions/${versionId}/deactivate`,
              undefined,
              { params }
          )
        : await api.post<ModelVersionSummary>(
              `/model-management/versions/${versionId}/deactivate`
          )
    return response.data
}

export const deleteModelVersion = async (
    versionId: number,
    context?: ModelVersionOperationLogContext
): Promise<ModelVersionSummary> => {
    const params = getModelVersionOperationLogParams(context)
    const response = params
        ? await api.delete<ModelVersionSummary>(
              `/model-management/versions/${versionId}`,
              { params }
          )
        : await api.delete<ModelVersionSummary>(
              `/model-management/versions/${versionId}`
          )
    return response.data
}

export const rollbackModelVersion = async (
    versionId: number,
    context?: ModelVersionOperationLogContext
): Promise<ModelVersionSummary> => {
    const params = getModelVersionOperationLogParams(context)
    const response = params
        ? await api.post<ModelVersionSummary>(
              `/model-management/versions/${versionId}/rollback`,
              undefined,
              { params }
          )
        : await api.post<ModelVersionSummary>(
              `/model-management/versions/${versionId}/rollback`
          )
    return response.data
}

export const getModelEvaluationTasks = async (
    params: {
        status?: string | null
        modelKey?: ModelKey | null
        projectId?: number | null
        limit?: number
        offset?: number
    } = {}
): Promise<ModelEvaluationTaskListResponse> => {
    const response = await api.get<ModelEvaluationTaskListResponse>(
        '/model-management/evaluations',
        {
            params: {
                limit: params.limit,
                model_key: params.modelKey,
                offset: params.offset,
                project_id: params.projectId,
                status: params.status,
            },
        }
    )
    return response.data
}

export const createModelEvaluationTask = async (
    payload: ModelEvaluationCreatePayload
): Promise<ModelEvaluationTaskDetail> => {
    const response = await api.post<ModelEvaluationTaskDetail>(
        '/model-management/evaluations',
        {
            evaluation_mode: 'v3',
            baseline_pipeline: toBackendEvaluationPipeline(
                payload.baselinePipeline
            ),
            candidate_pipeline: toBackendEvaluationPipeline(
                payload.candidatePipeline
            ),
            mapping_set_id: payload.mappingSetId ?? 'default',
            name: payload.name,
            project_id: payload.projectId,
            validation_dataset_stable_id: payload.validationDatasetStableId,
            validation_rule_stable_id: payload.validationRuleStableId,
        }
    )
    return response.data
}

export const getModelEvaluationTask = async (
    taskId: number
): Promise<ModelEvaluationTaskDetail> => {
    const response = await api.get<ModelEvaluationTaskDetail>(
        `/model-management/evaluations/${taskId}`
    )
    return response.data
}

export const getModelEvaluationImages = async (
    taskId: number,
    params: {
        status?: string | null
        limit?: number
        offset?: number
    } = {}
): Promise<ModelEvaluationImageResultListResponse> => {
    const response = await api.get<ModelEvaluationImageResultListResponse>(
        `/model-management/evaluations/${taskId}/images`,
        {
            params: {
                limit:
                    params.limit === undefined
                        ? undefined
                        : Math.min(params.limit, 200),
                offset: params.offset,
                status: params.status,
            },
        }
    )
    return response.data
}

export const cancelModelEvaluationTask = async (
    taskId: number
): Promise<ModelEvaluationTaskDetail> => {
    const response = await api.post<ModelEvaluationTaskDetail>(
        `/model-management/evaluations/${taskId}/cancel`
    )
    return response.data
}

export const rerunModelEvaluationTask = async (
    taskId: number
): Promise<ModelEvaluationTaskDetail> => {
    const response = await api.post<ModelEvaluationTaskDetail>(
        `/model-management/evaluations/${taskId}/rerun`
    )
    return response.data
}

export const downloadModelEvaluationReport = async (
    taskId: number,
    format: 'json' | 'csv' | 'xlsx'
): Promise<Blob> => {
    const response = await api.get<Blob>(
        `/model-management/evaluations/${taskId}/export`,
        {
            params: { format },
            responseType: 'blob',
        }
    )
    return response.data
}

export const getModelEvaluationV3Report = async (
    taskId: number
): Promise<ModelEvaluationV3Report> => {
    const response = await api.get<ModelEvaluationV3Report>(
        `/model-management/evaluations/${taskId}/report`
    )
    return response.data
}

export const getModelEvaluationMatches = async (
    taskId: number,
    params: {
        schemeSide?: 'baseline' | 'candidate'
        outcome?: string
        categoryKey?: string
        imageResultId?: number
        limit?: number
        offset?: number
    } = {}
): Promise<ModelEvaluationMatchListResponse> => {
    const response = await api.get<ModelEvaluationMatchListResponse>(
        `/model-management/evaluations/${taskId}/matches`,
        {
            params: {
                scheme_side: params.schemeSide,
                outcome: params.outcome,
                category_key: params.categoryKey,
                image_result_id: params.imageResultId,
                limit: params.limit,
                offset: params.offset,
            },
        }
    )
    return response.data
}
